import { db } from "../db/indexeddb";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";

/**
 * PO Repository
 * Handles:
 * - Cache storage
 * - Offline search
 * - Full API sync
 */
export class PORepository {
  getPOStatusCategory(po) {
    const finalStatus = String(po?.final_status || "").toLowerCase();
    const normalizedIsActive = String(po?.is_active).trim().toLowerCase();
    const isCancelled =
      po?.is_active === false ||
      po?.is_active === 0 ||
      normalizedIsActive === "false" ||
      normalizedIsActive === "0" ||
      finalStatus === "cancelled" ||
      finalStatus === "canceled";

    if (isCancelled) {
      return "cancelled";
    }

    if (finalStatus === "completed") {
      return "approved";
    }

    if (
      finalStatus === "in-progress" &&
      Number(po?.current_status?.app_status) === 2
    ) {
      return "approved";
    }

    if (
      (Number(po?.approval_count || 0) > 0 && po?.current_status == null) ||
      (finalStatus === "in-progress" &&
        Number(po?.current_status?.app_status) === 1 &&
        Number(po?.current_status?.re_generate) === 1)
    ) {
      return "waitingforapproval";
    }

    return "other";
  }

  matchesPOStatus(po, statusFilter = "") {
    const normalizedFilter = String(statusFilter || "")
      .replace(/\s+/g, "")
      .toLowerCase();

    if (!normalizedFilter) return true;
    const poStatusCategory = this.getPOStatusCategory(po);

    switch (normalizedFilter) {
      case "waitingforapproval":
        return poStatusCategory === "waitingforapproval";
      case "approved":
        return poStatusCategory === "approved";
      case "cancelled":
      case "cancel":
        return poStatusCategory === "cancelled";
      default:
        return true;
    }
  }

  /* ===============================
     Get all cached POs
  =============================== */
  async getPOsFromCache() {
    const data = await db.purchaseOrders.toArray();
    return data.map((d) => d.data);
  }

  /* ===============================
     Get single PO
  =============================== */
  async getPOByToken(poToken) {
    const res = await db.purchaseOrders
      .where("PO_token")
      .equals(poToken)
      .first();

    return res?.data || null;
  }

  /* ===============================
     Save PO cache
  =============================== */
async savePOsToCache(pos = [], clearOld = false) {
  if (!pos.length) return;

  const pendingDeletes = await db.pendingPODeletes.toArray();
  const deletedTokens = pendingDeletes.map(d => d.PO_token);

  const filteredPOs = pos.filter(
    po => !deletedTokens.includes(po.PO_token)
  );

  try {
    await db.transaction("rw", db.purchaseOrders, async () => {
      if (clearOld) {
        await db.purchaseOrders.clear();
      }
     
      const rows = filteredPOs.map(po => ({
        PO_token: po.PO_token,
        PO_number: po.PO_number,
        data: po,
        lastSynced: Date.now(),
        
      }));
    
    
    
      for (const row of rows) {
        await db.purchaseOrders.put(row);
    
         console.log("Updating PO:",rows.PO_token);
      }
    });

    console.log("Stored", filteredPOs.length, "POs");
  } catch (err) {
    console.error("IndexedDB save error:", err);
  }
}



  /* ===============================
     Sync ALL pages from API (smart)
  =============================== */
async syncAllPOsFromAPI(authToken, buyerToken, options = {}) {
  if (!navigator.onLine) {
    return this.getPOsFromCache();
  }

  const {
    currentPage = 1,
    currentPageData,
    pageCount: providedPageCount,
    totalCount: providedTotalCount,
    force = false,
  } = options || {};

  const cacheCount = await db.purchaseOrders.count();
  const cacheEmpty = cacheCount === 0;
  const cacheComplete = await this.getCacheComplete();
  const lastTotalCount = await this.getLastTotalCount();
  const lastTotalPages = await this.getLastTotalPages();

  if (!force && cacheComplete && !cacheEmpty) {
    return this.getPOsFromCache();
  }

  if (Array.isArray(currentPageData) && currentPageData.length) {
    await this.savePOsToCache(currentPageData, false);
  }

  const lastSyncedPage = await this.getLastSyncedPage();

  let totalPages = Number(providedPageCount || 0);
  let totalCount = Number(providedTotalCount || 0);
  let firstPagePayload = null;

  if (!totalPages) {
    console.log("Fetching PO page: 1 (sync check)");
    const response = await axios.post(
      API_ENDPOINT.get_purchase_order_list,
      { buyer_token: buyerToken, page: 1 },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );

    const payload = response?.data || {};
    const pageData = payload.results || [];
    totalPages = payload.page_count || 1;
    totalCount = payload.total_count || payload.total_po_count || totalCount;
    firstPagePayload = { pageData, totalPages };

    if (!Array.isArray(currentPageData) || currentPageData.length === 0) {
      await this.savePOsToCache(pageData, true);
      await this.setLastSyncedPage(1);
    }
  }

  if (!cacheEmpty && lastTotalPages && totalPages && totalPages < lastTotalPages) {
    console.log("PO pages reduced. Running full resync.");
    return this._fullSync(authToken, buyerToken, firstPagePayload);
  }

  const effectiveLastSyncedPage = cacheEmpty
    ? (Number(currentPage) === 1 && Array.isArray(currentPageData) && currentPageData.length ? 1 : 0)
    : lastSyncedPage;

  if (!cacheEmpty && totalPages && effectiveLastSyncedPage >= totalPages) {
    await this.setLastSyncedPage(totalPages);
    await this.setLastTotalPages(totalPages);
    if (totalCount) await this.setLastTotalCount(totalCount);
    const currentCount = await db.purchaseOrders.count();
    await this.setCacheComplete(totalCount ? currentCount >= totalCount : currentCount > 0);
    await this.setLastSyncAt(Date.now());
    return this.getPOsFromCache();
  }

  const startPage = Math.max(1, (effectiveLastSyncedPage || 0) + 1);
  const needsFullClear = startPage === 1;
  let allPOs = [];

  for (let page = startPage; page <= totalPages; page++) {
    if (page === 1 && firstPagePayload?.pageData) {
      await this.savePOsToCache(firstPagePayload.pageData, needsFullClear);
      await this.setLastSyncedPage(page);
      allPOs.push(...firstPagePayload.pageData);
      continue;
    }

    console.log("Fetching PO page:", page);
    const response = await axios.post(
      API_ENDPOINT.get_purchase_order_list,
      { buyer_token: buyerToken, page },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );

    const payload = response?.data || {};
    const pageData = payload.results || [];
    if (!totalCount) {
      totalCount = payload.total_count || payload.total_po_count || totalCount;
    }

    console.log(`Page ${page}/${totalPages} fetched`);
    await this.savePOsToCache(pageData, needsFullClear && page === 1);
    await this.setLastSyncedPage(page);
    allPOs.push(...pageData);
  }

  await this.setLastTotalPages(totalPages);
  if (totalCount) await this.setLastTotalCount(totalCount);
  const currentCount = await db.purchaseOrders.count();
  await this.setCacheComplete(totalCount ? currentCount >= totalCount : currentCount > 0);
  await this.setLastSyncAt(Date.now());

  console.log("PO sync complete");
  return allPOs.length ? allPOs : this.getPOsFromCache();
}

async _fullSync(authToken, buyerToken, firstPagePayload) {
  let page = 1;
  let totalPages = firstPagePayload?.totalPages || 1;
  let totalCount = 0;
  let allPOs = [];

  if (firstPagePayload?.pageData) {
    await this.savePOsToCache(firstPagePayload.pageData, true);
    await this.setLastSyncedPage(1);
    allPOs.push(...firstPagePayload.pageData);
    page = 2;
  }

  for (; page <= totalPages; page++) {
    console.log("Fetching PO page:", page);
    const response = await axios.post(
      API_ENDPOINT.get_purchase_order_list,
      { buyer_token: buyerToken, page },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );

    const payload = response?.data || {};
    const pageData = payload.results || [];
    totalPages = payload.page_count || totalPages || 1;
    totalCount = payload.total_count || payload.total_po_count || totalCount;

    console.log(`Page ${page}/${totalPages} fetched`);
    await this.savePOsToCache(pageData, page === 1);
    await this.setLastSyncedPage(page);
    allPOs.push(...pageData);
  }

  await this.setLastTotalPages(totalPages);
  if (totalCount) await this.setLastTotalCount(totalCount);
  const currentCount = await db.purchaseOrders.count();
  await this.setCacheComplete(totalCount ? currentCount >= totalCount : currentCount > 0);
  await this.setLastSyncAt(Date.now());
  console.log("All pages synced");
  return allPOs;
}





  /* ===============================
     Offline Search + Filter
  =============================== */
  async searchPOsOffline(
  searchQuery = "",
  statusFilter = "",
  pageNumber = 1,
  pageSize = 10
) {
  const allPOs = await this.getPOsFromCache();

  let filtered = allPOs;

  /* ---------- SEARCH ---------- */
  if (searchQuery) {
    const q = searchQuery.toLowerCase();

    filtered = filtered.filter(po =>
      po.PO_number?.toLowerCase().includes(q) ||
      po.vendor_name?.toLowerCase().includes(q) ||
      po.rfq_code?.toLowerCase().includes(q)
    );
  }

  /* ---------- STATUS FILTER ---------- */
  if (statusFilter) {
    filtered = filtered.filter((po) => this.matchesPOStatus(po, statusFilter));
  }

  /* ---------- PAGINATION ---------- */
  const start = (pageNumber - 1) * pageSize;
  const end = start + pageSize;

  const pageData = filtered.slice(start, end);

  return {
    results: pageData,
    total_count: filtered.length,
    page_count: Math.ceil(filtered.length / pageSize),
  };
}

// save pending updated data
async saveOfflinePOUpdate(formData) {
  await db.pendingPOUpdates.add({
    PO_token: formData.PO_token,
    payload: formData,
    createdAt: Date.now(),
  });

  console.log("Offline PO saved:", formData.PO_token);
}


async syncPendingPOUpdates(authToken) {
  const updates = await db.pendingPOUpdates.toArray();

  for (const update of updates) {
    try {
      await axios.post(
        API_ENDPOINT.update_purchase_order,
        update.payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      await db.pendingPOUpdates.delete(update.id);

      console.log("Offline PO synced");
    } catch (err) {
      console.error("Sync failed", err);
    }
  }
}


async deletePOOffline(poToken){
  await db.purchaseOrders.where("PO_token").equals(poToken).delete()

  await db.pendingPODeletes.add({
    PO_token:poToken,
    createdAT:Date.now()
  })
    console.log("PO deleted offline:", poToken);

}

async syncPendingPODeletes(authToken) {
  const deletes = await db.pendingPODeletes.toArray();
   console.log("Pending deletes:", deletes.length);

  for (const item of deletes) {
    try {
      console.log("Syncing delete:", item.PO_token);

      await axios.post(
        API_ENDPOINT.detele_purchase_order,
        { PO_token: item.PO_token },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      await db.pendingPODeletes.delete(item.id);

    console.log("Delete synced:", item.PO_token);
    } catch (err) {
      console.error("Delete sync failed", err);
    }
  }
}

//sync page
async setLastSyncedPage(page) {
  await db.syncMeta.put({
    key: "po_last_page",
    value: page
  });
}

async getLastSyncedPage() {
  const res = await db.syncMeta.get("po_last_page");
  return res?.value || 0;
}

async setLastTotalPages(totalPages) {
  await db.syncMeta.put({
    key: "po_last_total_pages",
    value: totalPages
  });
}

async getLastTotalPages() {
  const res = await db.syncMeta.get("po_last_total_pages");
  return res?.value || 0;
}

async setLastTotalCount(totalCount) {
  await db.syncMeta.put({
    key: "po_last_total_count",
    value: totalCount
  });
}

async getLastTotalCount() {
  const res = await db.syncMeta.get("po_last_total_count");
  return res?.value || 0;
}

async setCacheComplete(value) {
  await db.syncMeta.put({
    key: "po_cache_complete",
    value: Boolean(value)
  });
}

async getCacheComplete() {
  const res = await db.syncMeta.get("po_cache_complete");
  return Boolean(res?.value);
}
async setLastSyncAt(timestamp) {
  await db.syncMeta.put({
    key: "po_last_sync_at",
    value: timestamp
  });
}

async getLastSyncAt() {
  const res = await db.syncMeta.get("po_last_sync_at");
  return res?.value || 0;
}




}

export const poRepository = new PORepository();
